import Paper from '@/components/Paper'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import useDebounce from '@/hooks/useDebounce'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    getRegulationAcceptanceList,
    getRegulationsList,
} from '@/modules/Regulations/services'
import { AcceptanceStatusFilter } from '@/modules/Regulations/types'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { buildAcceptanceColumns } from './regulation-acceptance-columns'
import RegulationAcceptanceFilters from './regulation-acceptance-filters'

const DEFAULT_PAGE_SIZE = 20
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

const RegulationAcceptanceList: React.FC = () => {
    const { t } = useTranslation()
    const [status, setStatus] = useState<AcceptanceStatusFilter>('all')
    const [search, setSearch] = useState('')
    const [regulationVersion, setRegulationVersion] = useState('')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

    const debouncedSearch = useDebounce(search, 300)

    const { data: regulations = [] } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.REGULATIONS_LIST],
        queryFn: () => getRegulationsList(),
    })

    const versionOptions = useMemo(
        () =>
            regulations.map((r) => ({
                label: `v${r.regulation_version.replace(/^v/i, '')}${
                    r.is_active ? ` (${t('active')})` : ''
                }`,
                value: r.regulation_version,
            })),
        [regulations, t]
    )

    const { data, isFetching } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REGULATIONS_ACCEPTANCE_LIST,
            status,
            debouncedSearch,
            regulationVersion,
            page,
            pageSize,
        ],
        queryFn: () =>
            getRegulationAcceptanceList({
                status,
                search: debouncedSearch || undefined,
                regulation_version: regulationVersion || undefined,
                page,
                page_size: pageSize,
            }),
    })

    const columns = useMemo(() => buildAcceptanceColumns(t), [t])

    const results = data?.results || []
    const totalCount = data?.pagination?.total || 0

    const handleStatusChange = (next: AcceptanceStatusFilter) => {
        setStatus(next)
        setPage(1)
    }

    const handleSearchChange = (next: string) => {
        setSearch(next)
        setPage(1)
    }

    const handleVersionChange = (next: string) => {
        setRegulationVersion(next)
        setPage(1)
    }

    return (
        <Paper className="space-y-3">
            <RegulationAcceptanceFilters
                status={status}
                onStatusChange={handleStatusChange}
                search={search}
                onSearchChange={handleSearchChange}
                regulationVersion={regulationVersion}
                onRegulationVersionChange={handleVersionChange}
                versionOptions={versionOptions}
                currentActiveVersion={data?.current_active_version}
            />

            <DataTableCustomized
                columns={columns}
                rows={results}
                rowKey={(row) => row.employee_id}
                size="small"
                resizable
                isFetching={isFetching}
                loading={!results.length && isFetching}
                emptyMessage={t('settings:no-employees-found')}
                pagination={{
                    page,
                    pageSize,
                    total: totalCount,
                    pageSizeOptions: PAGE_SIZE_OPTIONS,
                    onPageChange: setPage,
                    onPageSizeChange: (size: number) => {
                        setPageSize(size)
                        setPage(1)
                    },
                }}
            />
        </Paper>
    )
}

export default RegulationAcceptanceList
